<template>
  <div class="lang-switch">
    <button
      v-for="lang in languages"
      :key="lang"
      type="button"
      :class="['lang-switch__item', { 'is-active': lang === value, 'is-full': isFull(lang), 'is-empty': isEmpty(lang) }]"
      :title="titleOf(lang)"
      @click="$emit('input', lang)"
    >
      {{ lang.toUpperCase() }}
      <span class="lang-switch__mark">{{ markOf(lang) }}</span>
    </button>
  </div>
</template>

<script>
import { EMPLOYEE_LANGUAGES } from "./employeeLanguages";

/**
 * One language switch for a whole block of translatable fields: the editor picks a language
 * once and every field of the block shows it. Each language shows how much of the block is
 * filled in it, so the empty ones are visible without opening them.
 *
 * `fields` is the block's translatable values: [{ lang: text, ... }, ...].
 */
export default {
  name: "LangSwitch",
  props: {
    value: { type: String, required: true },
    fields: { type: Array, default: () => [] },
    languages: { type: Array, default: () => EMPLOYEE_LANGUAGES },
  },
  methods: {
    filledCount(lang) {
      return this.fields.filter((translations) => {
        const text = (translations && translations[lang]) || "";

        // rich text of only tags / &nbsp; is empty for an editor
        return text.replace(/<[^>]*>|&nbsp;/g, "").trim() !== "";
      }).length;
    },
    isFull(lang) {
      return this.fields.length > 0 && this.filledCount(lang) === this.fields.length;
    },
    isEmpty(lang) {
      return this.filledCount(lang) === 0;
    },
    markOf(lang) {
      if (this.isFull(lang)) return "●";
      if (this.isEmpty(lang)) return "○";

      return `${this.filledCount(lang)}/${this.fields.length}`;
    },
    titleOf(lang) {
      return `${this.filledCount(lang)} of ${this.fields.length} fields filled`;
    },
  },
};
</script>

<style scoped>
.lang-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.lang-switch__item {
  border: 1px solid #dcdfe6;
  background: #fff;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  line-height: 20px;
  cursor: pointer;
  color: #606266;
}

.lang-switch__item.is-active {
  border-color: #409eff;
  background: #409eff;
  color: #fff;
}

.lang-switch__mark {
  margin-left: 2px;
  font-size: 11px;
}

.lang-switch__item.is-full:not(.is-active) .lang-switch__mark {
  color: #67c23a;
}

.lang-switch__item.is-empty:not(.is-active) .lang-switch__mark {
  color: #c0c4cc;
}
</style>
